/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import React from "react";
import {IconCube3d} from "../../../../../../../components/buttons/IconButtons";
import RegisteredByChip from "./RegisteredByChip";
import {
    getMaterialControlItemTypeTab,
    getMaterialRegisteredSource,
    getRegisteredByIcon
} from "../../../../Model/Model";

/**
 * Material of a dispatched movement: the direction it took at the gate and the
 * user that registered it.
 *
 * The material is read as a row of the movement, so its description takes the
 * room the row leaves and the direction it took is read at the end of it, above
 * the chip that names who wrote it.
 *
 * @param {Function} props.t - Translation handler.
 * @param {Object} props.folio - Folio the material belongs to.
 * @param {Object} props.item - Material being read.
 * @param {string} props.registeredBy - User that registered the material.
 * @param {number} props.registeredAt - Moment the material was registered.
 *
 * @returns {JSX.Element} Row of the material.
 */
const DispatchedMaterialRow = ({t, folio, item, registeredBy, registeredAt}) => {
    // The direction is the one the material was captured with, so the row reads
    // the material that came into the plant apart from the one that left it
    const typeTab = getMaterialControlItemTypeTab(item?.type);

    // Every material keeps the user that wrote it and the moment it was
    // written, so it is read before the user the rest of the log is named with
    const source = getMaterialRegisteredSource(item)
        || {name: registeredBy, date: registeredAt};

    return (
        <div className={"row background-shades-100 border-radius-8px p-2"}>
            <div className={"d-flex align-items-center justify-content-between"}>
                <div className={"d-flex align-items-center minw-0"}>
                    <div className={"background-primary-25 border-radius-4px mr-2 w-40px-h-40px d-flex align-items-center justify-content-center"}>
                        <IconCube3d width={40} height={40}/>
                    </div>
                    <span className={"fs-14px"}>{item?.name}</span>
                </div>

                {/* The direction of the material and the user that registered it
                    are read at the end of the row, so both are aligned to the
                    same edge. */}
                <div className={"d-flex flex-column align-items-end justify-content-center flex-shrink-0 pl-2"}>
                    {typeTab &&
                        <div className={"d-flex align-items-center justify-content-end mb-1"}>
                            <div className={"mr-1 d-flex align-items-center"}>{typeTab.icon}</div>
                            <p className={"fs-12px font-color-shades-700"}>{t(typeTab.translation)}</p>
                        </div>
                    }
                    <RegisteredByChip
                        Icon={getRegisteredByIcon(folio, source.name)}
                        name={source.name}
                        date={source.date}
                    />
                </div>
            </div>
        </div>
    );
};

export default DispatchedMaterialRow;
